/**
 * peekEscStack — ONE shared depth registry for stacked peeks (UOW-4G #3672).
 *
 * The problem it replaces: every host that stacked a peek over another
 * registered its OWN capture-phase window ESC listener. stopPropagation()
 * does not stop SIBLING listeners on the same node in the same phase, so at
 * depth 3 (PO peek → task peek → doc peek) two live capture listeners both
 * fired and one ESC closed two layers at once — and boundaries with no
 * handler at all (tracker → task) fell through to BOTH base shells'
 * bubble-phase listeners, closing everything together.
 *
 * The model: hosts register a layer while their stacked peek is open (push
 * on open, unregister on close via effect cleanup). A SINGLE window
 * capture-phase listener exists while any layer is registered; each ESC
 * stops propagation (so no bubble-phase shell listener fires) and closes
 * EXACTLY the top layer — last registered wins. When the stack empties the
 * listener unregisters and ESC reaches the base peek's own shell normally.
 *
 * The closed entry is popped optimistically (before React commits the
 * close), so a rapid second ESC deterministically targets the next layer
 * instead of a stale top; the host effect's cleanup then finds the entry
 * already gone and no-ops.
 *
 * ORDERING CONTRACT: stack order is registration order, which is EFFECT-
 * COMMIT order (LIFO on close) — a parent and child whose layers activate in
 * the SAME React commit register in that commit's effect order (parents'
 * effects run after children's), which would invert their ESC order; keep a
 * layer's activation tied to its own open transition rather than mounting
 * parent and child layers together in one commit.
 */

interface PeekLayerEntry {
  close: () => void
}

const stack: PeekLayerEntry[] = []
let listening = false

function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || stack.length === 0) return
  // Window's capture entry precedes its bubble entry in the dispatch path;
  // stopping propagation here means no shell's bubble listener ever fires.
  e.stopPropagation()
  const top = stack.pop()
  if (stack.length === 0) stopListening()
  top?.close()
}

function startListening() {
  if (listening) return
  window.addEventListener('keydown', onKeydown, true)
  listening = true
}

function stopListening() {
  if (!listening) return
  window.removeEventListener('keydown', onKeydown, true)
  listening = false
}

/**
 * Register one stacked layer. Returns the unregister function — call it when
 * the layer closes for ANY reason (effect cleanup handles this).
 */
export function registerPeekLayer(close: () => void): () => void {
  const entry: PeekLayerEntry = { close }
  stack.push(entry)
  startListening()
  return () => {
    const i = stack.indexOf(entry)
    if (i >= 0) stack.splice(i, 1)
    if (stack.length === 0) stopListening()
  }
}

/** Current registered depth — exported for tests. */
export function peekLayerDepth(): number {
  return stack.length
}
